import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useLocale } from "../i18n/useLocale";
import { useSwitchLocale } from "../i18n/useSwitchLocale";
import { useActiveLocale } from "../i18n/LocaleContext";
import { instanceFor } from "../i18n/instances";
import { consentDecided, onConsentDecided } from "../consent";
import { LIVE_LOCALES, type LocaleDef } from "../i18n/locales";

/**
 * LanguageSuggestionBanner (Story 4.4, FR23) — a lightweight, client-only,
 * dismissible nudge that offers to switch to the visitor's preferred language.
 *
 * Why client-only and never a redirect: prefix-all + x-default=nl is the routing
 * contract (i18n-architecture.md §3.3/§3.4). Server-side geo/Accept-Language
 * redirects are rejected (bad for SEO + cache), so the URL the visitor requested
 * is the URL they keep. The ONLY locale switch is this explicit, opt-in click,
 * routed through the shared `useSwitchLocale()` rule.
 *
 * Hydration safety: it depends on `navigator`/`localStorage` (client-only), so it
 * renders nothing on the server / first hydration render and only decides whether
 * to show AFTER mount, in an effect (the render-nothing-then-decide pattern), so
 * the prerendered HTML is byte-identical to before.
 */

const STORAGE_KEY = "doneit_locale";

/** Primary subtag, lowercased: "nl-BE" → "nl", "en" → "en". */
const primary = (tag: string) => tag.toLowerCase().split("-")[0];

/**
 * Does a browser language `tag` map to live locale `l`? Region-suffixed locale
 * codes (e.g. "fr-be") demand a REGION-EXACT match: a browser must send "fr-BE"
 * to be offered Belgian French — an international French visitor ("fr" / "fr-FR")
 * is NOT nudged into the Belgium market (law-2027, BE pricing). Plain codes
 * ("nl", "en") match by primary subtag, so they stay robust across the regional
 * variants a browser may report ("nl-NL"/"nl-BE" → nl).
 */
const matchesLocale = (l: LocaleDef, tag: string) =>
  l.code.includes("-")
    ? tag.toLowerCase() === l.code
    : primary(l.code) === primary(tag);

/**
 * Walk the browser's preferred languages in order and return the first that maps
 * to a LIVE locale (draft locales are excluded because they aren't in
 * LIVE_LOCALES). Returns null when the top match equals the current locale (their
 * preference is already satisfied — don't nag) or nothing maps to a live locale.
 */
function pickPreferredLiveLocale(currentCode: string): LocaleDef | null {
  if (typeof navigator === "undefined") return null;
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    if (!tag) continue;
    const match = LIVE_LOCALES.find((l) => matchesLocale(l, tag));
    if (match) return match.code === currentCode ? null : match;
  }
  return null;
}

export default function LanguageSuggestionBanner() {
  const active = useActiveLocale();
  const { locale } = useLocale();
  const switchLocale = useSwitchLocale();
  const [target, setTarget] = useState<LocaleDef | null>(null);
  /**
   * Wait for the cookie banner to be answered before offering a language switch.
   *
   * Not a z-index problem — below the library's breakpoint the consent banner is
   * full-width along the bottom whatever its `position` says, so this nudge sits
   * underneath it on every phone. Queueing behind it also spares a first-time
   * visitor two prompts at once, which is the better behaviour on desktop too
   * (DIT-402).
   */
  const [consentPending, setConsentPending] = useState(() => !consentDecided());
  useEffect(() => {
    // Re-read on mount as well as subscribing: `run()` is async, so consent can
    // resolve in the gap between the first render and this effect, and the event
    // would be gone by the time we listen for it.
    if (consentDecided()) setConsentPending(false);
    return onConsentDecided(() => setConsentPending(false));
  }, []);

  useEffect(() => {
    // Only inside a `:lang` subtree (real locale pages) — never on sandbox/demo
    // routes that sit outside LocaleLayout.
    if (!active) return;
    // A stored preference (set on accept or dismiss) suppresses the banner forever.
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch {}
    const preferred = pickPreferredLiveLocale(locale);
    if (preferred) setTarget(preferred);
  }, [active, locale]);

  if (!target || consentPending) return null;

  // AC9 — render the nudge in the TARGET language being offered (so a visitor who
  // can't read the current locale still understands the prompt). `getFixedT` binds
  // a `t` to the target locale's own fixed instance (no changeLanguage, NFR6-safe).
  const tt = instanceFor(target.code).getFixedT(target.code, "common");

  const persist = (code: string) => {
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {}
  };
  const accept = () => {
    persist(target.code);
    switchLocale(target.code);
  };
  const dismiss = () => {
    persist(locale);
    setTarget(null);
  };

  return (
    <div
      // Marked so the support launcher can measure it and step aside: on a phone
      // this grows to nearly the full width, and the launcher sat on its Switch
      // button — the one collision the spec names.
      data-support-chat-lift
      className="fixed bottom-4 left-4 z-50 flex max-w-[calc(100vw-2rem)] items-center gap-3 rounded-2xl border border-line bg-white px-4 py-3 shadow-lg"
    >
      <span className="text-[13px] font-medium text-ink">{tt("banner.switchTo", { name: target.name })}</span>
      <button
        onClick={accept}
        className="rounded-xl bg-primary px-3 py-1.5 text-[13px] font-semibold text-white transition-transform hover:scale-105 active:scale-95"
      >
        {tt("banner.switchCta")}
      </button>
      <button
        onClick={dismiss}
        aria-label={tt("banner.dismiss")}
        className="text-ink-soft transition-colors hover:text-ink"
      >
        <X size={16} strokeWidth={2} />
      </button>
    </div>
  );
}
